import { Link } from "wouter";
import type { AuditEvent } from "../../api/types";
import { useAuditEvents } from "../../api/queries";
import { LoadingState } from "../../components/AsyncStates";
import { StatePill } from "../../components/StatePill";
import { activityDescription, activityPill, relativeTime } from "./homeActivity";

const RECENT_ACTIVITY_LIMIT = 5;

/** Govern's Audit tab is the fallback destination for every row — see
 * GovernPage.tsx (`/govern` with no sub-path lands on Audit). */
const GOVERN_AUDIT_HREF = "/govern";

/**
 * The row's click-through target. Only resources this app can deep-link to
 * WITHOUT a second lookup get a specific destination (A-5 — no speculative
 * fetch just to build a link); everything else — including `service`/
 * `agent_session` events, whose detail routes are nested under a project id
 * this event doesn't carry — falls back to the Govern audit view, where the
 * full row (including that id) is still visible.
 */
function activityHref(event: AuditEvent): string {
  if (event.resource === "node" && event.target) {
    return `/hosts/${event.target}`;
  }
  return GOVERN_AUDIT_HREF;
}

/**
 * Home's "Recent activity" (IA slice 1, admin-only — see recentActivity.ts
 * for why). Deliberately renders NOTHING once loaded to an empty trail: a
 * fresh tenant's Home is the hero + three entry cards alone, not an empty
 * "no activity yet" box under them — that omission IS the empty-state story
 * (RFC §"What each destination absorbs" — Home ← the `/get-started`
 * empty-state story).
 *
 * UX polish wave 2 decision 5: a failed fetch here is deliberately quiet — a
 * one-line muted note, never the red `ErrorState` — because this is a
 * secondary widget on the Home hero, not the reason anyone opened this page
 * (unlike, say, MyServicesPage's table, where a failed fetch IS the content
 * the operator came for). The full trail — and a real retry — is one click
 * away in Govern regardless.
 */
export function RecentActivity() {
  const { data, isPending, isError } = useAuditEvents({ limit: RECENT_ACTIVITY_LIMIT });

  if (isPending) return <LoadingState label="Loading recent activity…" />;
  if (isError) {
    return (
      <p className="home-activity__note">
        Couldn't load recent activity — see{" "}
        <Link href={GOVERN_AUDIT_HREF}>the full trail in Govern</Link>.
      </p>
    );
  }
  if (data.items.length === 0) return null;

  return (
    <section className="home-activity" aria-labelledby="home-activity-heading">
      <p className="home-activity__label" id="home-activity-heading">
        Recent activity
      </p>
      <ul className="home-activity__list">
        {data.items.map((event) => {
          const pill = activityPill(event);
          return (
            <li key={event.id} className="home-activity__row">
              <Link href={activityHref(event)} className="home-activity__link">
                <StatePill label={pill.label} tone={pill.tone} shape="dot" />
                <span className="home-activity__description">{activityDescription(event)}</span>
                <span className="home-activity__time">{relativeTime(event.occurredAt)}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <Link href={GOVERN_AUDIT_HREF} className="home-activity__see-all">
        See all in Govern →
      </Link>
    </section>
  );
}
